import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { toastService } from '@services/toast.service.ts';
import {
  adminUsersService,
  AdminClientOption,
  ClientSubscriptionSummary,
} from '@services/admin-users.service.ts';

const formatDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString('uk-UA') : '—';

/**
 * Замінює стару DevSubscriptionTools (client-домен) — та била в легасі
 * client-payments/dev/set-status, який писав підписку напряму без запису
 * в journal транзакцій (client_payment_transactions) і без будь-якого
 * адмін-аудиту. Тут — ті самі два реальні бекенд-інструменти
 * (adminCreateVirtualPayment / adminResetSubscription), що поводяться як
 * справжня оплата: реальний nextPaymentDate, provider="virtual" видно в
 * історії транзакцій окремо від wayforpay/apple/google.
 *
 * Без вибору плану і циклу оплати: реально продається лише один план
 * (default_tier, помісячно) — бекенд сам його резолвить за кодом.
 */
export const AdminClientPaymentsDevTools: React.FC = () => {
  const [clients, setClients] = useState<AdminClientOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  const [selectedClient, setSelectedClient] = useState<AdminClientOption | null>(null);
  const [isTrial, setIsTrial] = useState(false);
  const [daysUntilNextPayment, setDaysUntilNextPayment] = useState('30');
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const [summary, setSummary] = useState<ClientSubscriptionSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    adminUsersService.getClientPaymentsOptions()
      .then((opts) => setClients(opts.clients))
      .finally(() => setOptionsLoading(false));
  }, []);

  const loadSummary = useCallback(async (userId: number) => {
    setSummaryLoading(true);
    try {
      const data = await adminUsersService.getClientSubscriptionSummary(userId);
      setSummary(data);
    } catch {
      toastService.error('Не вдалося завантажити стан клієнта');
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadSummary(selectedClient.value);
    } else {
      setSummary(null);
    }
  }, [selectedClient, loadSummary]);

  const handleCreateVirtualPayment = async () => {
    if (!selectedClient) return;
    setCreating(true);
    try {
      const days = daysUntilNextPayment.trim() === '' ? 30 : parseInt(daysUntilNextPayment, 10);
      const result = await adminUsersService.createVirtualPayment(selectedClient.value, {
        billingCycle: 'month',
        isTrial,
        daysUntilNextPayment: isNaN(days) ? 30 : days,
      });
      toastService.success(`Віртуальну оплату створено. Наступне списання: ${formatDate(result.nextPaymentDate)}`);
      loadSummary(selectedClient.value);
    } catch (error: any) {
      toastService.error(error?.response?.data?.message || 'Не вдалося створити віртуальну оплату');
    } finally {
      setCreating(false);
    }
  };

  const handleResetSubscription = async () => {
    setResetConfirmOpen(false);
    if (!selectedClient) return;
    setResetting(true);
    try {
      await adminUsersService.resetClientSubscription(selectedClient.value);
      toastService.success(`Підписку клієнта скинуто до початкового стану`);
      loadSummary(selectedClient.value);
    } catch (error: any) {
      toastService.error(error?.response?.data?.message || 'Не вдалося скинути підписку');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Box sx={{ borderLeft: '4px solid', borderLeftColor: 'warning.main', pl: 2 }}>
      <Typography variant="h6" gutterBottom>
        Dev Tools: тестові оплати клієнтів
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Створює реальний запис підписки/транзакції (provider = "virtual") на єдиний план, який зараз реально
        продається — поводиться як справжня оплата, термін дії реально перевіряється. Не плутати з
        billing-exempt (те повністю вимикає перевірку білінгу).
      </Typography>

      <Autocomplete
        options={clients}
        value={selectedClient}
        onChange={(_, value) => setSelectedClient(value)}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        loading={optionsLoading}
        size="small"
        sx={{ mb: 2, maxWidth: 480 }}
        renderInput={(params) => (
          <TextField {...params} label="Клієнт" placeholder="Пошук за іменем або email" />
        )}
      />

      {selectedClient && (
        <Box sx={{ mb: 3 }}>
          {summaryLoading ? (
            <CircularProgress size={20} />
          ) : summary ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Chip
                size="small"
                label={summary.status}
                color={summary.isActive ? 'success' : 'default'}
                variant="outlined"
              />
              {summary.isBillingExempt && <Chip size="small" label="billing-exempt" color="warning" variant="outlined" />}
              {summary.isTrial && <Chip size="small" label="тріал" color="info" variant="outlined" />}
              <Typography variant="body2" color="text.secondary">
                План: {summary.plan ?? '—'} · Сума: {summary.amount ?? '—'} {summary.currency ?? ''} ·
                {' '}Автопродовження: {summary.isAutoRenewal ? 'так' : 'ні'} ·
                {' '}Наступне списання: {formatDate(summary.nextPaymentDate)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">Немає даних</Typography>
          )}
        </Box>
      )}

      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={5}>
          <TextField
            label="Днів до списання"
            value={daysUntilNextPayment}
            onChange={(e) => setDaysUntilNextPayment(e.target.value)}
            type="number"
            size="small"
            fullWidth
            helperText="Від'ємне число — вже протухла підписка"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={<Switch checked={isTrial} onChange={(e) => setIsTrial(e.target.checked)} />}
            label="Тріал"
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleCreateVirtualPayment}
          disabled={creating || !selectedClient}
        >
          {creating ? 'Створення...' : 'Створити віртуальну оплату'}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" gutterBottom>
        Скидання стану
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Видаляє поточний запис підписки, скидає "тріал використано" і billingExempt — гарантований нульовий
        стан з точки зору білінгу, навіть якщо акаунт раніше отримував ручні пільги. Дозволяє повторно пройти
        весь чесний флоу (тріал → реальна оплата → протухання) з нуля. Історію транзакцій/спроб оплати,
        онбординг і email-верифікацію не чіпає.
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Незворотна дія — поточна підписка клієнта буде видалена.
      </Alert>
      <Button
        variant="outlined"
        color="error"
        onClick={() => setResetConfirmOpen(true)}
        disabled={resetting || !selectedClient}
      >
        {resetting ? 'Скидання...' : 'Скинути підписку до початкового стану'}
      </Button>

      <ConfirmationDialog
        open={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        onConfirm={handleResetSubscription}
        title="Скинути підписку?"
        message={`Підписку клієнта "${selectedClient?.label ?? ''}" буде видалено, прапорці "тріал використано" і billingExempt скинуто. Дію не можна відмінити.`}
        confirmText="Скинути"
      />
    </Box>
  );
};
